import React from 'react';
import { mui } from '@local/finsera-core/src/libs';

import LongTextDialog from './dialog';

type LongTextCellProps = {
  value: string;
  children: React.ReactNode;
  columnName?: string;
  showAsDialog?: boolean;
};

/**
 * Component for displaying cells with potentially very long text content
 * Uses tooltip for moderately long text and a dialog for very long text
 *
 * @param value - The cell value
 * @param columnName - The name of the column
 * @param showAsDialog - Whether to show the cell as a dialog
 * @returns Rendered component
 */
const LongTextCell = ({ value, columnName = 'Cell Content', showAsDialog = false, children }: LongTextCellProps) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  if (showAsDialog) {
    return (
      <>
        <mui.core.Box
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
            cursor: 'pointer',
            color: 'primary.main',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            setDialogOpen(true);
          }}
        >
          {children}
        </mui.core.Box>
        <LongTextDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          value={value}
          title={`${columnName}: Cell Content`}
        />
      </>
    );
  }

  return (
    <mui.core.Tooltip
      title={value}
      arrow
      placement="bottom-start"
      PopperProps={{
        sx: {
          '& .MuiTooltip-tooltip': {
            maxWidth: '500px',
            fontSize: '12px',
            padding: '8px 12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          },
        },
      }}
    >
      <mui.core.Box
        sx={{
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
      >
        {children}
      </mui.core.Box>
    </mui.core.Tooltip>
  );
};

export default LongTextCell;
